/**
 * Spinner - Loading indicator component
 *
 * Provides visual feedback during async operations.
 * Accessible with proper ARIA attributes.
 */

import React from 'react';
import { twMerge } from 'tailwind-merge';

interface SpinnerProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Color variant */
  color?: 'primary' | 'secondary' | 'white' | 'inherit';
  /** Accessible label for screen readers */
  label?: string;
  /** Show label visually */
  showLabel?: boolean;
  /** Center in container */
  centered?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const sizeClasses: Record<NonNullable<SpinnerProps['size']>, string> = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-9 h-9',
  xl: 'w-12 h-12',
};

const colorClasses: Record<NonNullable<SpinnerProps['color']>, string> = {
  primary: 'text-accent',
  secondary: 'text-text-secondary',
  white: 'text-text-inverse',
  inherit: 'text-current',
};

export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'primary',
  label = 'Loading...',
  showLabel = false,
  centered = false,
  className = '',
}) => {
  return (
    <div
      className={twMerge(
        'inline-flex items-center gap-2',
        centered && 'flex-col justify-center items-center gap-3',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div
        className={twMerge(
          'inline-block animate-spin motion-reduce:animate-none',
          sizeClasses[size],
          colorClasses[color],
        )}
        aria-hidden="true"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="block">
          <circle
            className="opacity-20"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
          />
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="45 200"
            strokeDashoffset="-35"
          />
        </svg>
      </div>
      {showLabel ? (
        <span className="text-sm text-text-secondary font-medium">{label}</span>
      ) : (
        <span className="sr-only">{label}</span>
      )}
    </div>
  );
};

interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether the button is in loading state */
  isLoading?: boolean;
  /** Loading text (replaces children when loading) */
  loadingText?: string;
  /** Spinner size */
  spinnerSize?: 'sm' | 'md';
  /** Button variant class */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  isLoading = false,
  loadingText,
  spinnerSize = 'sm',
  variant = 'primary',
  children,
  disabled,
  className = '',
  ...props
}) => {
  const variantClasses: Record<string, string> = {
    primary: 'bg-aegean text-white hover:bg-aegean/90',
    secondary: 'bg-transparent border border-border-light text-text-primary hover:bg-surface',
    tertiary: 'bg-transparent text-text-secondary hover:text-text-primary',
    danger: 'bg-danger text-white hover:bg-danger/90',
  };

  return (
    <button
      className={twMerge(
        'relative inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors',
        'disabled:opacity-50 disabled:pointer-events-none',
        variantClasses[variant],
        isLoading && 'cursor-wait',
        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className="inline-flex items-center">
          <Spinner size={spinnerSize} color="inherit" label="Loading" />
        </span>
      )}
      <span className={isLoading && loadingText ? 'sr-only' : ''}>
        {isLoading && loadingText ? loadingText : children}
      </span>
      {isLoading && loadingText && (
        <span aria-hidden="true">{loadingText}</span>
      )}
    </button>
  );
};
